import { shallowEqual, useSelector } from "react-redux";

import {
  hoverDataPointAtom,
  hoverLegendGroupAtom,
  hoverLegendIdAtom,
  hoverLegendItemAtom
} from "atoms/interaction";
import { useAtomValue } from "jotai";
import { RootState } from "store/rootReducer";
import {
  selectAttentionWells,
  selectSelectedWells,
  selectTxnId,
  useMapStore
} from "stores/map";
import {
  selectCheckedGlobalForecasts,
  selectCheckedGlobalTypeWells,
  useForecastStore
} from "stores/forecast";

import { EntityKind } from "models/entityKind";

/**
 * Composite selector for non-hover chart dependencies used by the shared/multiproduct
 * chart components.
 *
 * Hover fields (hoverLegendItem, hoverLegendGroup, hoverLegendId, hoverDataPoint)
 * have been removed from this Redux selector and are now sourced from Jotai atoms so
 * that pointer-frequency hover updates no longer trigger a Redux re-render cascade.
 *
 * Phase 1 of STATE_MANAGEMENT_MIGRATION_PLAN.md.
 */
const selectChartDependencies = (state: RootState, entityKind: EntityKind) => {
  const isWell = entityKind === EntityKind.Well;

  return {
    normalizeTypeWell: state.app.normalizeTypeWell,
    pdenDataSourceSetting: state.app.pdenDataSourceSetting,
    activeColorPalette: isWell
      ? state.userSetting.activeColorPalette
      : state.userSetting.activeFacilityColorPalette,
    globalNormalizeBy: state.normalizeBy.globalNormalizeBy,
    groupBy: isWell ? state.groupBy.globalGroupBy : state.groupBy.globalFacilityFocus,
    selectedGroups: state.groupBy.selectedGroups,
    syncWells: state.app.syncWells,
    userForecastSettings: state.app.userForecastSettings,
    useNormalizeBy: state.normalizeBy.useNormalizeBy,
    lockedColors: state.app.lockedColors,
    filterId: isWell ? state.filter.filterId : state.facilityFilter.filterId,
    sortBy: isWell ? state.filter.sortBy : state.facilityFilter.sortBy,
    showGroupsNotInFilter: isWell
      ? state.filter.showGroupsNotInFilter
      : state.facilityFilter.showGroupsNotInFilter,
    forecastMode: state.app.forecastMode,
    useForecastStartDate: state.app.useForecastStartDate
  };
};

/**
 * useChartDependencies (Shared Charts version)
 *
 * Provides all chart dependencies for the shared / multiproduct chart components.
 * The return shape is identical to the pre-migration version; hover fields are now
 * sourced from Jotai atoms instead of Redux so pointer-frequency updates no longer
 * cause a Redux re-render.
 *
 * PERFORMANCE:
 *  - Non-hover fields: single Redux subscription with shallowEqual (1 subscription
 *    instead of the previous 20+).
 *  - Hover fields: individual Jotai atom subscriptions — only components that read
 *    a specific hover field re-render when that field changes.
 *
 * @param entityKind - Well or Facility, selects the correct filter / groupBy slices.
 */
const useChartDependencies = (entityKind: EntityKind) => {
  // Single Redux subscription for all stable / query-driving state (non-map).
  const reduxDeps = useSelector(
    (state: RootState) => selectChartDependencies(state, entityKind),
    shallowEqual
  );

  // Map fields sourced from Zustand — migrated from Redux.
  const attentionWells = useMapStore(selectAttentionWells);
  const selectedWells = useMapStore(selectSelectedWells);
  const txnId = useMapStore(selectTxnId);

  // Forecast fields sourced from ForecastStore — migrated from Redux.
  const checkedGlobalForecasts = useForecastStore(selectCheckedGlobalForecasts);
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);

  // Hover fields sourced from Jotai — pointer-frequency, narrow subscriptions.
  const hoverLegendItem = useAtomValue(hoverLegendItemAtom);
  const hoverLegendGroup = useAtomValue(hoverLegendGroupAtom);
  const hoverLegendId = useAtomValue(hoverLegendIdAtom);
  // atom uses [] as the canonical empty state — no null coercion needed.
  const hoverDataPoint = useAtomValue(hoverDataPointAtom);

  return {
    ...reduxDeps,
    checkedGlobalForecasts,
    checkedGlobalTypeWells,
    attentionWells,
    selectedWells,
    txnId,
    hoverDataPoint,
    hoverLegendItem,
    hoverLegendGroup,
    hoverLegendId
  };
};

export default useChartDependencies;
